import PropTypes from 'prop-types';
import React, {
    useState,
    useMemo,
    useCallback,
    useRef,
    useEffect,
} from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';

import {
    NoKeyPlaceholder,
    LoadErrorPlaceholder,
    LoadingSpinner,
} from './MapStateShell';

/**
 * Lookup table mapping common geospatial bound descriptions to map center
 * coordinates and zoom levels. Used as fallback when no GeoJSON is provided.
 */
const REGION_CENTERS = [
    // Most specific patterns first to avoid false matches
    {
        keywords: ['colorado river basin', 'colorado river'],
        center: { lat: 37.5, lng: -110.5 },
        zoom: 6,
    },
    {
        keywords: ['denver metro', 'denver'],
        center: { lat: 39.74, lng: -104.99 },
        zoom: 10,
    },
    {
        keywords: ['arkansas river', 'lower arkansas'],
        center: { lat: 38.1, lng: -104.0 },
        zoom: 8,
    },
    {
        keywords: ['state of colorado', 'colorado'],
        center: { lat: 39.0, lng: -105.5 },
        zoom: 7,
    },
];

const DEFAULT_CENTER = { lat: 39.0, lng: -105.5 };
const DEFAULT_ZOOM = 7;
const OVERLAY_COLOR = '#0d6efd';

/**
 * The Google Maps API requires an inline style object for the container.
 * This is the one exception to the no-inline-styles rule.
 */
const MAP_CONTAINER_STYLE = {
    width: '100%',
    height: '200px',
    borderRadius: '0.375rem',
};

const MAP_OPTIONS = {
    disableDefaultUI: true,
    zoomControl: true,
    gestureHandling: 'cooperative',
};

/**
 * Resolves a geospatial description string to a center/zoom configuration
 * by matching against known region keywords.
 */
function resolveRegion(description) {
    if (!description) {
        return { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM };
    }

    const normalized = description.toLowerCase().trim();

    for (const region of REGION_CENTERS) {
        for (const keyword of region.keywords) {
            if (normalized.includes(keyword)) {
                return { center: region.center, zoom: region.zoom };
            }
        }
    }

    return { center: DEFAULT_CENTER, zoom: DEFAULT_ZOOM };
}

/**
 * Normalizes a Polygon or MultiPolygon geometry's coordinates to the
 * MultiPolygon shape (`[polygon][ring][position]`), so centerFromGeojson and
 * geojsonToPaths only need to walk one shape. A GeoJSON Polygon's
 * coordinates (`[ring][position]`) are one level shallower than a
 * MultiPolygon's -- feeding them to a MultiPolygon-shaped loop without this
 * normalization silently destructures a numeric [lng, lat] pair as if it
 * were itself a ring.
 */
function normalizedPolygons(geojson) {
    if (!geojson || !geojson.coordinates) return [];
    if (geojson.type === 'Polygon') return [geojson.coordinates];
    return geojson.coordinates;
}

/**
 * Compute center and zoom from a GeoJSON Polygon or MultiPolygon so the
 * shape fits nicely within the map viewport.
 */
export function centerFromGeojson(geojson) {
    if (!geojson || !geojson.coordinates) return null;

    let minLat = 90;
    let maxLat = -90;
    let minLng = 180;
    let maxLng = -180;

    for (const polygon of normalizedPolygons(geojson)) {
        for (const ring of polygon) {
            for (const [lng, lat] of ring) {
                if (lat < minLat) minLat = lat;
                if (lat > maxLat) maxLat = lat;
                if (lng < minLng) minLng = lng;
                if (lng > maxLng) maxLng = lng;
            }
        }
    }

    const center = {
        lat: (minLat + maxLat) / 2,
        lng: (minLng + maxLng) / 2,
    };

    // Estimate zoom from bounding box size
    const latSpan = maxLat - minLat;
    const lngSpan = maxLng - minLng;
    const maxSpan = Math.max(latSpan, lngSpan);

    let zoom = DEFAULT_ZOOM;
    if (maxSpan > 10) zoom = 5;
    else if (maxSpan > 5) zoom = 6;
    else if (maxSpan > 2) zoom = 7;
    else if (maxSpan > 1) zoom = 8;
    else if (maxSpan > 0.5) zoom = 9;
    else zoom = 10;

    return { center, zoom };
}

/**
 * Convert GeoJSON Polygon or MultiPolygon coordinates to an array of
 * google.maps.LatLng-compatible path arrays for Polygon overlays.
 */
export function geojsonToPaths(geojson) {
    if (!geojson || !geojson.coordinates) return [];

    const paths = [];
    for (const polygon of normalizedPolygons(geojson)) {
        for (const ring of polygon) {
            paths.push(ring.map(([lng, lat]) => ({ lat, lng })));
        }
    }
    return paths;
}

/**
 * Displays a Google Map showing the geographic coverage area for a data source
 * or product. When a GeoJSON geometry is provided, renders it as a polygon
 * overlay. Otherwise falls back to centering based on a text description.
 *
 * Falls back to a placeholder card when no API key is configured or when the
 * map fails to load.
 */
export default function GeoBoundaryMap({ description, geojson, className }) {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const polygonRef = useRef(null);
    const [map, setMap] = useState(null);

    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: apiKey || '',
    });

    // Compute center/zoom: prefer GeoJSON bounds, fall back to text lookup
    const { center, zoom } = useMemo(() => {
        const fromGeo = geojson ? centerFromGeojson(geojson) : null;
        if (fromGeo) return fromGeo;
        return resolveRegion(description);
    }, [description, geojson]);

    // Paths for the polygon overlay (empty if no geojson)
    const paths = useMemo(() => geojsonToPaths(geojson), [geojson]);

    const onMapLoad = useCallback((mapInstance) => {
        setMap(mapInstance);
    }, []);

    // Draw polygon overlay imperatively when map + paths are ready.
    // Uses state (not ref) for the map instance so this effect re-runs
    // regardless of whether the map or the data arrives first.
    useEffect(() => {
        if (!map || !window.google || paths.length === 0) return;

        // Clear previous polygon
        if (polygonRef.current) {
            polygonRef.current.setMap(null);
        }

        polygonRef.current = new window.google.maps.Polygon({
            paths,
            map,
            fillColor: OVERLAY_COLOR,
            fillOpacity: 0.12,
            strokeColor: OVERLAY_COLOR,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            clickable: false,
        });

        return () => {
            if (polygonRef.current) {
                polygonRef.current.setMap(null);
                polygonRef.current = null;
            }
        };
    }, [map, paths]);

    const displayText = description || 'Geographic coverage area';

    // No API key configured -- show a static placeholder
    if (!apiKey) {
        return (
            <NoKeyPlaceholder displayText={displayText} className={className} />
        );
    }

    // Google Maps script failed to load
    if (loadError) {
        return (
            <LoadErrorPlaceholder
                displayText={displayText}
                className={className}
            />
        );
    }

    // Google Maps script is still loading
    if (!isLoaded) {
        return (
            <LoadingSpinner displayText={displayText} className={className} />
        );
    }

    return (
        <div
            className={className || ''}
            role="img"
            aria-label={`Map showing geographic coverage: ${displayText}`}
        >
            <GoogleMap
                mapContainerStyle={MAP_CONTAINER_STYLE}
                center={center}
                zoom={zoom}
                options={MAP_OPTIONS}
                onLoad={onMapLoad}
            />
        </div>
    );
}

GeoBoundaryMap.propTypes = {
    /** Text description of the geographic boundary (e.g. "State of Colorado") */
    description: PropTypes.string,
    /** GeoJSON MultiPolygon geometry to render as a polygon overlay */
    geojson: PropTypes.shape({
        type: PropTypes.string,
        coordinates: PropTypes.array,
    }),
    /** Optional CSS class name(s) applied to the wrapper element */
    className: PropTypes.string,
};
